import React, { useState } from 'react';
import { Check, Edit3, RotateCcw, User, Phone, Calendar, Heart, AlertTriangle, ShieldCheck, ArrowRight, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export function OcrResultConfirmation({
  result,
  onConfirm,
  onRetake,
  onManualEntry,
  isSubmitting = false
}) {
  const isRecognized = result?.success && Boolean(result?.patient_name);
  const initialName = result?.patient_name || '';
  const initialAge = result?.age ? String(result.age) : '';
  const initialGender = result?.gender || '';
  const initialPhone = result?.phone || '';

  const [isEditing, setIsEditing] = useState(!isRecognized);
  const [editedName, setEditedName] = useState(initialName);
  const [editedAge, setEditedAge] = useState(initialAge);
  const [editedGender, setEditedGender] = useState(initialGender);
  const [editedPhone, setEditedPhone] = useState(initialPhone);

  const confidenceScore = result?.confidence ? Math.round(result.confidence * 100) : 0;
  const confidenceLevel = result?.confidence_level || 'LOW';

  const handleConfirmClick = () => {
    const finalName = editedName.trim();
    if (!finalName) return;
    onConfirm({
      name: finalName,
      age: editedAge.trim() ? parseInt(editedAge.trim(), 10) : null,
      gender: editedGender.trim() || null,
      phone: editedPhone.trim() || null,
    });
  };

  return (
    <div className="w-full max-w-xl mx-auto bg-card text-card-foreground rounded-2xl border border-border shadow-xl overflow-hidden p-6 sm:p-8 animate-in fade-in zoom-in-95 duration-200">
      {/* Header Badge */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          {isRecognized ? (
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 text-xs font-medium">
              <ShieldCheck className="h-3.5 w-3.5" />
              <span>Name Recognized ({confidenceScore}% score)</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 text-xs font-medium">
              <AlertTriangle className="h-3.5 w-3.5" />
              <span>Low Confidence Extraction</span>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={onRetake}
          disabled={isSubmitting}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 disabled:opacity-50"
        >
          <RotateCcw className="h-3 w-3" />
          <span>Retake Photo</span>
        </button>
      </div>

      {isRecognized && !isEditing ? (
        /* Confident Recognition View */
        <div className="space-y-6">
          <div className="text-center py-5 bg-muted/40 rounded-xl border border-border/60 px-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-1.5">
              Patient Details Detected
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl font-bold text-foreground tracking-tight">
              {editedName}
            </h2>
            
            {/* Demographic attributes badges */}
            <div className="flex items-center justify-center flex-wrap gap-2 mt-3">
              {editedAge ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
                  <Calendar className="w-3 h-3 text-muted-foreground" />
                  {editedAge} Yrs
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs text-muted-foreground bg-muted/60">
                  Age not specified
                </span>
              )}
              {editedGender ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
                  <Heart className="w-3 h-3 text-muted-foreground" />
                  {editedGender}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs text-muted-foreground bg-muted/60">
                  Gender not specified
                </span>
              )}
              {editedPhone ? (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground">
                  <Phone className="w-3 h-3 text-muted-foreground" />
                  {editedPhone}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs text-muted-foreground bg-muted/60">
                  Phone not specified
                </span>
              )}
            </div>

            <p className="text-xs text-muted-foreground mt-3">
              Confirm or edit details before proceeding with prescription upload.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={() => setIsEditing(true)}
              className="flex-1 h-12 flex items-center justify-center gap-2"
            >
              <Edit3 className="h-4 w-4" />
              <span>Edit Details</span>
            </Button>

            <Button
              type="button"
              disabled={isSubmitting}
              onClick={handleConfirmClick}
              className="flex-1 h-12 bg-primary text-primary-foreground hover:bg-primary/90 flex items-center justify-center gap-2 font-medium shadow-md"
            >
              <Check className="h-4 w-4" />
              <span>{isSubmitting ? 'Uploading...' : 'Confirm Details'}</span>
            </Button>
          </div>
        </div>
      ) : (
        /* Manual Entry / Edit View */
        <div className="space-y-5">
          <div>
            <h3 className="font-serif text-xl font-semibold mb-1">
              {isRecognized ? 'Edit Patient Information' : 'Enter Patient Information'}
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {isRecognized
                ? 'Review or fill in optional patient demographic details extracted from the prescription.'
                : "Enter patient details written on the prescription slip. Name is required; age, gender, and phone number are optional."}
            </p>
          </div>

          <div className="space-y-4">
            {/* Full Name (Mandatory) */}
            <div className="space-y-1.5">
              <Label htmlFor="patient-name-input" className="text-xs font-medium">
                Patient Full Name <span className="text-rose-500">*</span>
              </Label>
              <div className="relative">
                <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="patient-name-input"
                  autoFocus
                  disabled={isSubmitting}
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  placeholder="e.g. Rajesh Kumar"
                  className="pl-10 h-11 text-sm font-medium"
                />
              </div>
            </div>

            {/* Age & Gender Row (Optional) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="patient-age-input" className="text-xs font-medium text-muted-foreground">
                  Age <span className="text-[10px] text-muted-foreground">(Optional)</span>
                </Label>
                <div className="relative">
                  <Calendar className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="patient-age-input"
                    type="number"
                    min="0"
                    max="150"
                    disabled={isSubmitting}
                    value={editedAge}
                    onChange={(e) => setEditedAge(e.target.value)}
                    placeholder="e.g. 45"
                    className="pl-10 h-11 text-sm"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="patient-gender-select" className="text-xs font-medium text-muted-foreground">
                  Gender <span className="text-[10px] text-muted-foreground">(Optional)</span>
                </Label>
                <div className="relative">
                  <Heart className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                  <select
                    id="patient-gender-select"
                    disabled={isSubmitting}
                    value={editedGender}
                    onChange={(e) => setEditedGender(e.target.value)}
                    className="w-full h-11 pl-10 pr-3 rounded-md border border-input bg-background text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 text-foreground"
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Phone Number (Optional) */}
            <div className="space-y-1.5">
              <Label htmlFor="patient-phone-input" className="text-xs font-medium text-muted-foreground">
                Phone Number <span className="text-[10px] text-muted-foreground">(Optional)</span>
              </Label>
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="patient-phone-input"
                  type="tel"
                  disabled={isSubmitting}
                  value={editedPhone}
                  onChange={(e) => setEditedPhone(e.target.value)}
                  placeholder="e.g. 9876543210"
                  className="pl-10 h-11 text-sm"
                />
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              disabled={isSubmitting}
              onClick={onRetake}
              className="flex-1 h-11 flex items-center justify-center gap-2"
            >
              <RotateCcw className="h-4 w-4" />
              <span>Retake Photo</span>
            </Button>

            <Button
              type="button"
              disabled={!editedName.trim() || isSubmitting}
              onClick={handleConfirmClick}
              className="flex-1 h-11 bg-primary text-primary-foreground hover:bg-primary/90 flex items-center justify-center gap-2 font-medium"
            >
              <Check className="h-4 w-4" />
              <span>{isSubmitting ? 'Uploading...' : 'Confirm Details'}</span>
            </Button>
          </div>
        </div>
      )}

      {/* Manual Prescription Direct Fallback */}
      <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
        <span>Prefer to skip photo attachment?</span>
        <button
          type="button"
          onClick={onManualEntry}
          className="text-primary hover:underline font-medium flex items-center gap-1"
        >
          <span>Create Digital Prescription</span>
          <ArrowRight className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}
